import { Avatar, Box, Grid, IconButton, Menu, MenuItem } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CollapsibleFilterCard from "../../components/CollapsibleFilterCard/CollapsibleFilterCard";
import CustomButton, {
  CustomButtonVariant,
} from "../../components/CustomButton/CustomButton";
import CustomCard from "../../components/CustomCard/CustomCard";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import { StyledIcon } from "../../components/StyledIcon/StyledIcon";
import { routesConfig } from "../../config/routes";
import { getFirstDayOfCurrentMonth, getTitleAndValue } from "../../utils";
import { getFormattedTimestamp } from "../../utils/dateFormatter";
import { CommonMenuList } from "../ConsultQuotaOrders/models/OptionsMenu";
import ModalScheduleRequestLoader from "../RoadScheduleRequest/Section/ModalScheduleRequestLoader";
import ClearedRoadDeliveries from "./Sections/ClearedRoadDeliveries";
import OpenedRoadDeliveries from "./Sections/OpenedRoadDeliveries";
import RejectedRoadDeliveries from "./Sections/RejectedRoadDeliveries";
import { TableRow } from "./models/default";
import CalendarRange from "../../components/CalendarRange/CalendarRange";
import { TypesPermissions } from "../../components/Routes/TypesPermissions";
import { Roles } from "../../components/SideBar/Content/ListItem";
import { useAuth } from "../../contexts/authentication/AuthenticationContext";
import { AuthenticationContextData } from "../../contexts/authentication/domain";
import MultipleSelectDistributors from "../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import { useDistributor } from "../../contexts/distributor/DistributorContext";
import { sidebar } from "../../utils/featureFlags";
import { useFlags } from "flagsmith/react";
import ModalScheduleUpdate from "../RoadScheduleRequest/Section/ModalScheduleUpdate";
import { PipelineScheduleStatus } from "../../modules/operational/models/GetPipelineSchedulesDTO";
import { operationModeList } from "../RoadScheduleRequest/Section/RoadScheduleRequestStep1";
import SelectSubsidiaries from "../../components/SelectSubsidiary/SelectSubsidiary";

const RoadDeliveriesPage: React.FC = () => {
  const flags = useFlags(sidebar);
  const currentDay = new Date();
  const { selectedDistributorsStoraged, subsidiaries } = useDistributor();
  const [openedCount, setOpenedCount] = useState(0);
  const [clearedCount, setClearedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [dateFilterFrom, setDateFilterFrom] = useState<Date | null>(null);
  const [dateFilterTo, setDateFilterTo] = useState<Date | null>(null);
  const [invoiceIsEdit, setInvoiceIsEdit] = useState(false);
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({});
  const [statusData, setStatusData] = useState<PipelineScheduleStatus | null>(
    null
  );
  const { authByPermission, permissionSet, roles } =
    useAuth() as AuthenticationContextData;
  const [clearValueCalendar, setClearValueCalendar] = useState<boolean>(false);

  const handleClose = (open: boolean) => {
    setIsOpen(open);
  };

  const handleCloseUpdate = (open: boolean) => {
    setIsOpenUpdate(open);
  };

  const clearFilters = () => {
    setDateFilterFrom(null);
    setDateFilterTo(null);
    setClearValueCalendar(true);
    setCodeSubsidiary(undefined);
  };

  const isEditingInvoiceReference = useMemo(() => {
    const editableStatus = [
      PipelineScheduleStatus.INVOICE_PENDING,
      PipelineScheduleStatus.IN_SCHEDULING,
      PipelineScheduleStatus.IN_LOADING,
    ];

    return statusData !== null && editableStatus.includes(statusData);
  }, [statusData]);

  const buttonsModalFechar = [
    {
      title: "Fechar",
      onClick: () => {
        handleClose(false);
      },
      variant: CustomButtonVariant.CONTAINED,
    },
  ];

  const buttonsModalEditar = [
    {
      title: "Editar",
      onClick: () => {
        handleClose(false);
        handleCloseUpdate(true);
      },
      variant: invoiceIsEdit
        ? CustomButtonVariant.CONTAINED
        : CustomButtonVariant.DISABLED,
    },
    ...buttonsModalFechar,
  ];

  const selectedDistributors = useMemo(() => {
    return selectedDistributorsStoraged.join(",");
  }, [selectedDistributorsStoraged]);

  const isAllowed = useMemo(() => {
    return (
      authByPermission(
        permissionSet.SYSTEM_MODULES,
        roles.has(Roles.ADMIN),
        TypesPermissions.ROAD_STATIONS_REQUEST
      ) && flags.operational_new_road_stations.enabled
    );
  }, [authByPermission, flags]);

  const StyledMenu: React.FC<{ rowData: TableRow }> = ({ rowData }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuItemClick = async () => {
      setAnchorEl(null);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const menuOptions = [
      ...CommonMenuList.map((item) => ({
        ...item,
        onClick: () => {
          if (
            rowData?.customerInvoiceReference === "" &&
            rowData?.attachments === undefined
          ) {
            setInvoiceIsEdit(true);
          } else {
            setInvoiceIsEdit(false);
          }
          setStatusData(rowData.statusText);
          setModalData((oldState) => ({
            ...oldState,
            local: {
              title: "Nome da razão social",
              value: rowData.customer ?? "",
            },
            requestNumber: {
              title: "N. do pedido",
              value: rowData.scheduleNumber ?? "",
            },
            requestDate: getTitleAndValue(
              "Data do pedido",
              rowData.scheduleDate
            ),
            requestDateAndHours: getTitleAndValue(
              "Data do pedido",
              rowData.createdAt
            ),

            requestUser: getTitleAndValue(
              "Solicitado por:",
              rowData.customerRequester
            ),
            productName: getTitleAndValue("Nome do produto", rowData.product),
            modality: getTitleAndValue("Modalidade", rowData.mod),
            quantity: getTitleAndValue(
              "Quantidade",
              `${rowData.quantity} ${rowData.unit}`
            ),
            shippingCompany: getTitleAndValue(
              "Transportadora",
              rowData.shippingCompany
            ),
            driver: getTitleAndValue("Nome do motorista", rowData.driver),
            vehicle_plate: getTitleAndValue(
              "Placa do cavalo",
              rowData.vehicle_plate
            ),
            horse_plate: getTitleAndValue(
              "Placa da carreta",
              rowData.horse_plate
            ),
            requestNote: getTitleAndValue(
              "Observação",
              rowData.customerRequesterNote
            ),
            idSchedule: getTitleAndValue("idSchedule", rowData.id),
            invoiceReferenceNumber: getTitleAndValue(
              "Número da Nota Fiscal",
              rowData.customerInvoiceReference ?? ""
            ),
            invoicesFiles: getTitleAndValue(
              "Nota Fiscal",
              rowData.attachments && rowData.attachments.length > 0
                ? rowData.attachments[0]?.fileName ?? undefined
                : undefined
            ),
            paymentCondition: getTitleAndValue(
              "Condição de pagamento",
              rowData.paymentCondition ?? ""
            ),
            type: getTitleAndValue(
              "Modo de Operação",
              operationModeList.find((item) => item.id === rowData.type)?.label
            ),
            subsidiaryDescription: getTitleAndValue(
              "Filial",
              rowData.subsidiary ?? "-"
            ),
          }));
          setIsOpen(true);
        },
      })),
    ];

    return (
      <>
        <IconButton onClick={handleClickListItem}>
          <StyledIcon iconType="three-vertical-points" />
        </IconButton>
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "lock-button",
            role: "listbox",
          }}
          sx={{
            minWidth: 300,
          }}
        >
          {menuOptions.map((option, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                handleMenuItemClick();
                option.onClick();
              }}
              sx={{
                minWidth: 200,
                fontSize: 14,
                fontFamily: "Open Sans",
                "&.Mui-selected": {
                  color: "shadesOfDark.dark",
                  backgroundColor: "shadesOfDark.ultraLight",
                  fontWeight: 700,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "shadesOfDark.ultraLight",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QueryPageTitleCard
                title="Entregas rodoviárias"
                componentItem={
                  <CustomButton
                    title="Novo pedido de agendamento"
                    onClick={() => {
                      navigate(
                        routesConfig.OPERATIONAL().ROAD_STATIONS_REQUEST
                      );
                    }}
                    variant={
                      isAllowed
                        ? CustomButtonVariant.CONTAINED
                        : CustomButtonVariant.DISABLED
                    }
                    disabled={!isAllowed}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CollapsibleFilterCard
                title="Pedidos de agendamento"
                subtitle={getFormattedTimestamp(new Date())}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultipleSelectDistributors
                      maxWidth={1200}
                      label={"Selecione o cliente"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SelectSubsidiaries
                      label="Filial"
                      valueSubsidiary={subsidiaries.find(
                        (item) => item.id === codeSubsidiary
                      )}
                      onChangeValue={(selectedOption) => {
                        if (selectedOption) {
                          setCodeSubsidiary(selectedOption.id);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <CalendarRange
                      name="date"
                      label="Data de agendamento"
                      clearValueCalendar={clearValueCalendar}
                      firstDayOfMonth={getFirstDayOfCurrentMonth()}
                      lastDayOfMonth={currentDay}
                      onChangeDateStartEnd={(dateBegin, dateEnd) => {
                        setClearValueCalendar(false);
                        // eslint-disable-next-line
                        if (dateBegin && dateEnd) {
                          setDateFilterFrom(dateBegin);
                          setDateFilterTo(dateEnd);
                        }
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="flex-end"
                    gap={1}
                  >
                    <CustomButton
                      title="Limpar filtros"
                      variant={CustomButtonVariant.OUTLINED}
                      onClick={clearFilters}
                    />
                  </Grid>
                </Grid>
              </CollapsibleFilterCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <ModalScheduleUpdate
                  handleClose={(open) => handleCloseUpdate(open)}
                  cleanFilter={clearFilters}
                  isOpen={isOpenUpdate}
                  data={modalData}
                />
                <ModalScheduleRequestLoader
                  isEdit={isEditingInvoiceReference}
                  handleClose={(open) => handleClose(open)}
                  isOpen={isOpen}
                  data={modalData}
                  buttons={
                    isEditingInvoiceReference
                      ? buttonsModalEditar
                      : buttonsModalFechar
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTabs
                  tabsHeader={[
                    `Em aberto(${openedCount})`,
                    `Faturado(${clearedCount})`,
                    `Reprovado(${rejectedCount})`,
                  ]}
                  body={[
                    <OpenedRoadDeliveries
                      key={0}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setOpenedCount(count);
                      }}
                      menuIcon={(item) => <StyledMenu rowData={item} />}
                    />,
                    <ClearedRoadDeliveries
                      key={1}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setClearedCount(count);
                      }}
                      menuIcon={(item) => <StyledMenu rowData={item} />}
                    />,
                    <RejectedRoadDeliveries
                      key={2}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setRejectedCount(count);
                      }}
                      menuIcon={(item) => <StyledMenu rowData={item} />}
                    />,
                  ]}
                />
              </Grid>
            </Grid>
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default RoadDeliveriesPage;
